import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Trophy, Medal, Star, Eye, Flame, Zap, Camera, ShoppingBag, Gem, Sparkles, Rocket, Crown } from "lucide-react";

interface BadgeNotification {
  badge_id: string;
  name: string;
  description: string;
  icon: string;
  tier: number;
}

// Map icon names to emoji/components
const getBadgeEmoji = (tier: number) => {
  switch (tier) {
    case 4: return "💎"; // Diamond
    case 3: return "🏆"; // Gold
    case 2: return "🥈"; // Silver
    case 1: return "🥉"; // Bronze
    default: return "⭐";
  }
};

const STORAGE_KEY = "pd_seen_badge_ids";

const getStoredBadgeIds = (): string[] => {
  if (typeof window === "undefined") return [];

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.debug("Failed to read stored badge ids", error);
    return [];
  }
};

export function useBadgeNotifications() {
  const seenBadgesRef = useRef<Set<string>>(new Set(getStoredBadgeIds()));

  const markBadgeAsSeen = (badgeId: string) => {
    if (!badgeId) return;
    seenBadgesRef.current.add(badgeId);

    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(Array.from(seenBadgesRef.current))
      );
    }
  };

  const notifyBadge = (badge: any) => {
    if (!badge?.id) return;
    if (seenBadgesRef.current.has(badge.id)) return;

    markBadgeAsSeen(badge.id);
    showBadgeNotification(badge);
  };

  useEffect(() => {
    let channel: ReturnType<typeof supabase.channel> | null = null;

    const setupNotifications = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Subscribe to new badges for the current user
      channel = supabase
        .channel(`badge_notifications_${user.id}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'contributor_badges',
            filter: `user_id=eq.${user.id}`,
          },
          async (payload) => {
            const newBadge = payload.new as any;
            
            // Fetch badge details
            const { data: badgeType } = await supabase
              .from('badge_types' as any)
              .select('*')
              .eq('id', newBadge.badge_id)
              .single();

            if (badgeType) {
              notifyBadge({ ...badgeType, id: newBadge.badge_id });
            }
          }
        )
        .subscribe();

      // Check for any badges earned since last login
      checkRecentBadges(user.id);
    };

    setupNotifications();

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, []);

  const checkRecentBadges = async (userId: string) => {
    try {
      // Get badges earned in the last 24 hours that user might not have seen
      const twentyFourHoursAgo = new Date();
      twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);

      const { data: recentBadges } = await supabase
        .from('contributor_badges' as any)
        .select(`
          id,
          earned_at,
          badge_types:badge_id (
            id,
            name,
            description,
            icon,
            tier
          )
        `)
        .eq('user_id', userId)
        .gte('earned_at', twentyFourHoursAgo.toISOString())
        .order('earned_at', { ascending: false });

      // Show notifications for recent badges (max 3 to avoid spam)
      if (recentBadges && recentBadges.length > 0) {
        const badgesToShow = recentBadges.slice(0, 3);
        badgesToShow.forEach((badge: any, index: number) => {
          setTimeout(() => {
            notifyBadge(badge.badge_types);
          }, index * 1500); // Stagger notifications
        });
      }
    } catch (error) {
      console.error('Error checking recent badges:', error);
    }
  };

  const showBadgeNotification = (badge: any) => {
    const emoji = getBadgeEmoji(badge.tier);
    const tierName = ["", "Bronze", "Silver", "Gold", "Diamond"][badge.tier] || "";
    
    toast.success(
      `${emoji} New Badge Unlocked!`,
      {
        description: `${badge.name}\n${badge.description}`,
        duration: 6000,
        className: "badge-notification",
        style: {
          background: badge.tier === 4 ? 'linear-gradient(135deg, #06b6d4 0%, #2563eb 100%)' :
                     badge.tier === 3 ? 'linear-gradient(135deg, #fbbf24 0%, #f97316 100%)' :
                     badge.tier === 2 ? 'linear-gradient(135deg, #d1d5db 0%, #9ca3af 100%)' :
                     'linear-gradient(135deg, #fdba74 0%, #fb923c 100%)',
          color: 'white',
          border: '2px solid rgba(255,255,255,0.3)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        },
      }
    );

    // Play a celebration sound (optional)
    playBadgeSound(badge.tier);
  };

  const playBadgeSound = (tier: number) => {
    try {
      // Create a simple achievement sound using Web Audio API
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Different frequencies for different tiers
      const frequencies = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
      const frequency = frequencies[tier - 1] || 523.25;

      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      // Silently fail if audio doesn't work
      console.debug('Audio not supported or failed:', error);
    }
  };
}

